import { defineStore } from 'pinia'
import { ref, computed, type Ref, type ComputedRef } from 'vue'

import { usePageStore } from './page'
import { setCookie, getCookie } from './utils/helpers'

// Types and interfaces
interface AdSlotMarker {
  offsetTop: number,
  [key: string]: any
}

interface AdSlotMarkers {
  [offsetTop: number]: AdSlotMarker
}

interface AdSizePrediction {
  [key: string]: any
}

type TestZone = 'develop' | 'production' | string

const forceTestZoneByCookie = (): boolean => {
  return getCookie('forceTestZone') === 'develop'
}

export const useAdaStore = defineStore('ada', () => {
  // Stores
  const pageStore = usePageStore()

  // State
  const forceTestZone: Ref<boolean> = ref(false)
  const dynamicAdSlotList: Ref<any[]> = ref([])
  const adSizePrediction: Ref<AdSizePrediction> = ref({})
  const adSlotMarkers: Ref<AdSlotMarkers> = ref({})
  const activatedAdSlotMarkers: Ref<any[]> = ref([])
  const referenceOffset: Ref<number> = ref(0)

  // Getters
  const getDynamicAdSlotList: ComputedRef<any[]> = computed(() => {
    return dynamicAdSlotList.value
  })

  const asideEnabled: ComputedRef<boolean> = computed(() => {
    return (pageStore.pageData?.pageMeta?.pageType === 'article' || pageStore.pageData?.metaData?.behaveLikeArticle)
  })

  const isForceTestZone: ComputedRef<boolean> = computed(() => {
    return forceTestZone.value
  })

  const getAdSlotMarkers: ComputedRef<AdSlotMarkers> = computed(() => {
    return adSlotMarkers.value
  })

  const getActivatedAdSlotMarkers: ComputedRef<any[]> = computed(() => {
    return activatedAdSlotMarkers.value
  })

  const getReferenceOffset: ComputedRef<number> = computed(() => {
    return referenceOffset.value
  })

  // Actions
  const setDynamicAdSlotList = (adSlotList: any[]): void => {
    dynamicAdSlotList.value = adSlotList
  }

  const setForceTestZone = (newEnabled: boolean): void => {
    forceTestZone.value = newEnabled
  }

  const setTestZone = (testZone: TestZone): void => {
    setCookie('forceTestZone', testZone, new Date(Date.now() + 1000 * 60 * 60 * 24))
    setForceTestZone(testZone === 'develop')
  }

  const setAdSizePrediction = (payload?: AdSizePrediction): void => {
    adSizePrediction.value = payload || {}
  }

  const addAdSlotMarker = (payload: AdSlotMarker): void => {
    adSlotMarkers.value[payload.offsetTop] = payload
  }

  const setActivatedAdSlotMarkers = (payload: any[]): void => {
    activatedAdSlotMarkers.value = payload
  }

  const setReferenceOffset = (payload: number): void => {
    referenceOffset.value = payload
  }

  const checkForceTestZoneCookie = (): void => {
    setForceTestZone(forceTestZoneByCookie())
  }

  return {
    forceTestZone,
    adSizePrediction,
    adSlotMarkers,
    activatedAdSlotMarkers,
    referenceOffset,
    isForceTestZone,
    asideEnabled,
    getDynamicAdSlotList,
    getAdSlotMarkers,
    getActivatedAdSlotMarkers,
    getReferenceOffset,
    setForceTestZone,
    setTestZone,
    setAdSizePrediction,
    addAdSlotMarker,
    setActivatedAdSlotMarkers,
    checkForceTestZoneCookie,
    setDynamicAdSlotList,
    setReferenceOffset
  }
})
